import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react"
import { useDispatch } from "react-redux"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import { openSnackbar as openGlobalSnackbar } from "../../../reducers/snackbar"
import CallAttemptLogDialog from "../candidate-details/CallAttemptLogDialog"
import GlobalCallBar from "./GlobalCallBar"
import { amdAutoLogged, amdInitialDisposition, amdOutcomeMessage } from "./amd"
import { CALL_EVENTS } from "./callEvents"

const GlobalCallContext = createContext(null)

export function useGlobalCall() {
  const context = useContext(GlobalCallContext)
  if (!context) {
    throw new Error("useGlobalCall must be used within GlobalCallProvider")
  }
  return context
}

export function GlobalCallProvider({ children }) {
  const dispatch = useDispatch()
  const axios = useAuthorizedAxiosClient()
  const callbacksRef = useRef({})
  const amdStatusRef = useRef(null)
  const hangUpImplRef = useRef(null)
  const [callCandidate, setCallCandidate] = useState(null)
  const [callSessionOpen, setCallSessionOpen] = useState(false)
  const [attemptLogOpen, setAttemptLogOpen] = useState(false)
  const [attemptLogInSidePanel, setAttemptLogInSidePanel] = useState(false)
  const [pendingCallSid, setPendingCallSid] = useState(null)
  const [pendingCallDisposition, setPendingCallDisposition] =
    useState("no_answer")

  const startCall = useCallback((candidate, callbacks = {}) => {
    callbacksRef.current = callbacks || {}
    amdStatusRef.current = null
    setCallCandidate(candidate)
    setCallSessionOpen(true)
  }, [])

  const registerHangUp = useCallback((fn) => {
    hangUpImplRef.current = fn
  }, [])

  const finishAttempt = useCallback((updatedCall) => {
    const amdStatus = amdStatusRef.current
    callbacksRef.current.onAttemptFinished?.(updatedCall || null, { amdStatus })
    if (updatedCall) {
      callbacksRef.current.onAttemptLogged?.(updatedCall)
    }
    amdStatusRef.current = null
    setCallCandidate(null)
    callbacksRef.current = {}
  }, [])

  /**
   * The agent leg can drop the moment the backend redirects the lead (voicemail
   * handled), which may beat the status poll. Re-read once at call end so the
   * decision below is never made on a stale snapshot.
   */
  const finalizeAmdStatus = useCallback(
    (meta) => {
      const status = meta.amdStatus || null
      if (!meta.amdEnabled) return Promise.resolve(null)
      if (amdAutoLogged(status)) return Promise.resolve(status)
      const sid = status?.twilio_sid || meta.callSid
      if (!sid) return Promise.resolve(status)
      return axios
        .get(`/crm/api/calls/${encodeURIComponent(sid)}/amd/`)
        .then(({ data }) => data)
        .catch(() => status)
    },
    [axios]
  )

  /**
   * Voicemail handled by the backend (text sent, message left, hung up): the
   * Call row is already dispositioned, so skip the attempt dialog and hand the
   * saved row to the caller.
   */
  const completeAutoLoggedAttempt = useCallback(
    (amdStatus, callSid) => {
      const sid = amdStatus?.twilio_sid || callSid
      const fallback = {
        id: amdStatus?.call_id ?? null,
        twilio_sid: sid || null,
        disposition: amdStatus?.disposition || "voicemail",
        amd_result: amdStatus?.amd_result || null,
        amd_action: amdStatus?.amd_action || null,
        amd_auto_logged: true,
      }
      if (!sid) {
        finishAttempt(fallback)
        return
      }
      axios
        .get(`/crm/api/calls/${encodeURIComponent(sid)}/`)
        .then(({ data }) => finishAttempt({ ...fallback, ...data }))
        .catch(() => finishAttempt(fallback))
    },
    [axios, finishAttempt]
  )

  const abortCall = useCallback(() => {
    callbacksRef.current = {}
    amdStatusRef.current = null
    if (hangUpImplRef.current) {
      hangUpImplRef.current({
        skipAttemptLog: true,
        skipAttemptCallback: true,
      })
    }
    setCallSessionOpen(false)
    setAttemptLogOpen(false)
    setPendingCallSid(null)
    setPendingCallDisposition("no_answer")
    setCallCandidate(null)
  }, [])

  const handleCallSessionClosed = useCallback(
    (callEvent, meta = {}) => {
      setCallSessionOpen(false)
      if (meta.skipAttemptCallback) {
        setAttemptLogOpen(false)
        setPendingCallSid(null)
        setCallCandidate(null)
        amdStatusRef.current = null
        callbacksRef.current = {}
        return
      }
      callbacksRef.current.onCallEnded?.(callEvent, meta)
      const wantsAttemptLog =
        !meta.skipAttemptLog &&
        (callEvent === CALL_EVENTS.disconnected ||
          callEvent === CALL_EVENTS.cancelled)

      finalizeAmdStatus(meta).then((amdStatus) => {
        amdStatusRef.current = amdStatus
        const autoLogged = amdAutoLogged(amdStatus)
        if (callEvent === CALL_EVENTS.disconnected && !autoLogged) {
          dispatch(openGlobalSnackbar({ message: "Candidate ended the call" }))
        }
        if (wantsAttemptLog && autoLogged) {
          dispatch(
            openGlobalSnackbar({ message: amdOutcomeMessage(amdStatus) })
          )
          completeAutoLoggedAttempt(amdStatus, meta.callSid)
          return
        }
        if (wantsAttemptLog) {
          setPendingCallSid(meta.callSid || null)
          setPendingCallDisposition(
            amdInitialDisposition(amdStatus) || "no_answer"
          )
          setAttemptLogInSidePanel(Boolean(callbacksRef.current.logInSidePanel))
          setAttemptLogOpen(true)
          return
        }
        finishAttempt(null)
      })
    },
    [completeAutoLoggedAttempt, dispatch, finalizeAmdStatus, finishAttempt]
  )

  const handleAttemptLogClosed = useCallback(
    (updatedCall) => {
      setAttemptLogOpen(false)
      setPendingCallSid(null)
      setPendingCallDisposition("no_answer")
      if (updatedCall) {
        dispatch(openGlobalSnackbar({ message: "Call attempt logged" }))
      }
      finishAttempt(updatedCall || null)
    },
    [dispatch, finishAttempt]
  )

  const value = useMemo(
    () => ({
      startCall,
      abortCall,
      isCallActive: callSessionOpen,
      isAttemptLogOpen: attemptLogOpen,
      isBusy: callSessionOpen || attemptLogOpen,
      callCandidate,
    }),
    [abortCall, startCall, callSessionOpen, attemptLogOpen, callCandidate]
  )

  return (
    <GlobalCallContext.Provider value={value}>
      {children}
      <GlobalCallBar
        open={callSessionOpen}
        candidate={callCandidate}
        onSessionClose={handleCallSessionClosed}
        registerHangUp={registerHangUp}
      />
      <CallAttemptLogDialog
        open={attemptLogOpen}
        onClose={handleAttemptLogClosed}
        candidate={callCandidate || {}}
        callSid={pendingCallSid}
        initialDisposition={pendingCallDisposition}
        sidePanel={attemptLogInSidePanel}
      />
    </GlobalCallContext.Provider>
  )
}

export { CALL_EVENTS } from "./callEvents"
